/**
 * PN BUNDLE POPUP
 *
 * Offers the second piece immediately after an eligible product is added to the cart: pants when a
 * top goes in, tops when a bottom goes in.
 *
 * Trigger rule is derived from the active automatic discount ("Buy 1, get 1 — $X off", no per-order
 * limit): N eligible units earn floor(N / 2) discounts, so adding one more only earns a NEW discount
 * when the current eligible count is ODD. Even counts are already fully paired and the popup stays
 * shut — otherwise it would promise a saving the cart will not apply.
 *
 * Shopify takes the percentage off the lower-priced piece of the pair, so the saving shown is worked
 * out from both prices rather than from the offered piece alone.
 *
 * Listens for `variant:add` rather than `cart:change` because only the former carries the items that
 * were actually just added, and quantity edits inside the cart should not interrupt anyone.
 */

if (!window.customElements.get('pn-bundle-popup')) {
  class PnBundlePopup extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-pnb-products]');

      try {
        this.products = JSON.parse(dataEl.textContent);
      } catch (error) {
        this.products = [];
      }

      if (this.products.length === 0) return;

      this.eligibleIds = new Set(this.products.map((product) => product.id));
      this.percent = Number(this.dataset.percent) || 0;
      this.currency = this.dataset.currency || 'AUD';
      this.maxShown = Number(this.dataset.max) || 8;
      this.visible = [];
      this.pairPrice = 0;
      this.chosen = 0;
      this.declinedAt = null;
      this.lastFocus = null;

      this.panel = this.querySelector('.pnb__panel');
      this.hero = this.querySelector('[data-pnb-hero]');
      this.swatchRow = this.querySelector('[data-pnb-swatches]');
      this.swatches = Array.from(this.querySelectorAll('.pnb__swatch'));
      this.nameEl = this.querySelector('[data-pnb-name]');
      this.variantEl = this.querySelector('[data-pnb-variant]');
      this.wasEl = this.querySelector('[data-pnb-was]');
      this.nowEl = this.querySelector('[data-pnb-now]');
      this.sizeEl = this.querySelector('[data-pnb-size]');
      this.addBtn = this.querySelector('[data-pnb-add]');
      this.addLabel = this.querySelector('[data-pnb-add-label]');
      this.errorEl = this.querySelector('[data-pnb-error]');
      this.bagImg = this.querySelector('[data-pnb-bag-image]');
      this.bagName = this.querySelector('[data-pnb-bag-name]');
      this.saveEl = this.querySelector('[data-pnb-save]');

      this.addIdleLabel = this.addLabel.textContent;

      this.onVariantAdd = this.onVariantAdd.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      document.addEventListener('variant:add', this.onVariantAdd);
      document.addEventListener('keydown', this.onKeydown);

      this.querySelector('[data-pnb-close]').addEventListener('click', () => this.decline());
      this.querySelector('[data-pnb-decline]').addEventListener('click', () => this.decline());
      this.addEventListener('mousedown', (event) => {
        if (event.target === this) this.decline();
      });

      this.swatchRow.addEventListener('click', (event) => {
        const button = event.target.closest('.pnb__swatch');
        if (button) this.choose(Number(button.dataset.index), true);
      });

      this.swatchRow.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!step || this.visible.length === 0) return;
        event.preventDefault();
        const position = this.visible.indexOf(this.chosen);
        this.choose(this.visible[(position + step + this.visible.length) % this.visible.length], true);
      });

      this.addBtn.addEventListener('click', () => this.add());
    }

    disconnectedCallback() {
      document.removeEventListener('variant:add', this.onVariantAdd);
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* --- Trigger ------------------------------------------------------------------------------ */

    eligibleUnits(cart) {
      return (cart.items || []).reduce(
        (total, item) => (this.eligibleIds.has(item.product_id) ? total + item.quantity : total),
        0
      );
    }

    onVariantAdd(event) {
      const detail = event.detail || {};
      const cart = detail.cart;
      if (!cart || this.open) return;

      const added = (detail.items || []).filter((item) => this.eligibleIds.has(item.product_id));
      if (added.length === 0) return;

      const units = this.eligibleUnits(cart);

      // Even means every eligible shirt is already paired — nothing more to earn.
      if (units % 2 === 0) return;

      // They already said no at this exact cart state; do not ask twice.
      if (this.declinedAt === units) return;

      this.shownAtUnits = units;
      this.show(cart, added[added.length - 1]);
    }

    /* --- Content ------------------------------------------------------------------------------ */

    productById(id) {
      return this.products.find((product) => product.id === id);
    }

    fillBag(item) {
      const product = this.productById(item.product_id);
      const parts = [item.product_title || (product && product.title)];

      if (product && product.colour) parts.push(product.colour);
      if (item.variant_title && item.variant_title !== 'Default Title') parts.push(item.variant_title);

      this.bagName.textContent = parts.filter(Boolean).join(' · ');

      const image = item.featured_image ? item.featured_image.url : item.image;

      if (image) {
        this.bagImg.src = image;
        this.bagImg.alt = '';
        this.bagImg.hidden = false;
      } else {
        this.bagImg.hidden = true;
      }
    }

    /**
     * The other half of the outfit: pieces of a different type to the one just added (pants for a
     * top, tops for a bottom), in stock, not already in the cart. Falls back to any other in-stock
     * piece if that leaves nothing.
     */
    offerIndexes(cart, addedItem) {
      const inCart = new Set((cart.items || []).map((item) => item.product_id));
      const added = this.productById(addedItem.product_id);
      const addedType = added ? added.type : addedItem.product_type;
      const candidates = this.products
        .map((product, index) => ({ product, index }))
        .filter(({ product }) => product.available && !inCart.has(product.id));

      const otherType = candidates.filter(({ product }) => product.type !== addedType);
      const picked = otherType.length > 0 ? otherType : candidates;

      return picked.slice(0, this.maxShown).map(({ index }) => index);
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'narrowSymbol'
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    choose(index, focus) {
      const product = this.products[index];
      if (!product) return;

      const changed = index !== this.chosen;
      this.chosen = index;

      this.swatches.forEach((button, position) => {
        button.setAttribute('aria-checked', position === index ? 'true' : 'false');
        button.tabIndex = position === index ? 0 : -1;
      });

      if (focus) this.swatches[index].focus();

      if (changed) {
        this.hero.style.opacity = '0';
        setTimeout(() => {
          this.hero.src = product.image;
          this.hero.alt = product.title;
          this.hero.style.opacity = '1';
        }, 190);
      } else {
        this.hero.src = product.image;
        this.hero.alt = product.title;
        this.hero.style.opacity = '1';
      }

      this.nameEl.textContent = product.title;
      this.variantEl.textContent = product.colour || '';
      const saving = Math.floor((Math.min(this.pairPrice, product.price) * this.percent) / 100);
      this.wasEl.textContent = this.formatMoney(product.price);
      this.nowEl.textContent = this.formatMoney(product.price - saving);
      if (this.saveEl) this.saveEl.textContent = this.formatMoney(saving);

      this.sizeEl.innerHTML = '';
      product.variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.available ? variant.title : `${variant.title} — Sold out`;
        option.disabled = !variant.available;
        this.sizeEl.append(option);
      });

      const firstAvailable = product.variants.find((variant) => variant.available);
      if (firstAvailable) this.sizeEl.value = String(firstAvailable.id);
    }

    /* --- Open / close ------------------------------------------------------------------------- */

    show(cart, addedItem) {
      this.visible = this.offerIndexes(cart, addedItem);
      if (this.visible.length === 0) return;

      this.pairPrice = addedItem.price || 0;
      this.swatches.forEach((button, index) => {
        button.hidden = !this.visible.includes(index);
      });

      this.fillBag(addedItem);
      this.chosen = this.visible[0];
      this.choose(this.visible[0], false);

      this.errorEl.textContent = '';
      this.lastFocus = document.activeElement;
      this.open = true;
      this.hidden = false;
      this.classList.remove('is-closing');

      // rAF alone is throttled in background tabs, which would skip the entrance.
      requestAnimationFrame(() => this.classList.add('is-open'));
      setTimeout(() => this.classList.add('is-open'), 60);

      document.documentElement.classList.add('lock');
      setTimeout(() => this.panel.focus({ preventScroll: true }), 240);
    }

    hide() {
      this.classList.remove('is-open');
      this.classList.add('is-closing');
      document.documentElement.classList.remove('lock');

      setTimeout(() => {
        this.classList.remove('is-closing');
        this.hidden = true;
        this.open = false;
        this.addBtn.classList.remove('is-busy');
        this.addBtn.disabled = false;
        this.addLabel.textContent = this.addIdleLabel;
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      }, 420);
    }

    /** Remember the exact cart state they refused, so we ask again only if it changes. */
    decline() {
      this.declinedAt = this.shownAtUnits;
      this.hide();
    }

    onKeydown(event) {
      if (this.hidden || !this.open) return;

      if (event.key === 'Escape') {
        this.decline();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.panel.querySelectorAll(
        'button:not(:disabled):not([tabindex="-1"]), select, [href]'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* --- Add ---------------------------------------------------------------------------------- */

    async add() {
      if (this.addBtn.disabled) return;

      const variantId = this.sizeEl.value;
      if (!variantId) return;

      this.errorEl.textContent = '';
      this.addBtn.classList.add('is-busy');
      this.addBtn.disabled = true;
      this.addLabel.textContent = this.addBtn.dataset.busyLabel || 'Adding';

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || 'Could not add that size.');
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        this.addBtn.classList.remove('is-busy');
        this.addLabel.textContent = this.addBtn.dataset.doneLabel || 'Added';
        setTimeout(() => this.hide(), 900);
      } catch (error) {
        this.addBtn.classList.remove('is-busy');
        this.addBtn.disabled = false;
        this.addLabel.textContent = this.addIdleLabel;
        this.errorEl.textContent = error.message;
      }
    }
  }

  window.customElements.define('pn-bundle-popup', PnBundlePopup);
}
